import { useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { useUser } from '@taki/auth';
import { LoginScreen } from './LoginScreen.js';
import { BootScreen } from './surfaces/BootScreen.js';

export interface AuthGuardProps {
  config: TakiConfig;
  children: ReactNode;
}

/**
 * Gates the shell on authentication state from `@taki/auth`'s `useUser()`:
 *  - on the very first auth resolution, render the {@link BootScreen} splash;
 *  - when no user is connected, render {@link LoginScreen};
 *  - otherwise render the protected children (the shell).
 *
 * Boot latch: `useUser().loading` starts `true`, flips `false` once the first
 * auth state resolves, and can flip back to `true` on a genuine re-auth (a
 * fresh sign-in after a sign-out). The boot screen must never come back after
 * startup, so `bootedRef` latches once we ever leave the loading state: a
 * later loading phase keeps the shell mounted (the user is already set) rather
 * than re-flashing the splash.
 */
export function AuthGuard({ config, children }: AuthGuardProps): JSX.Element {
  const { user, loading } = useUser();

  // Monotonic latch: true forever once the first resolution happens.
  const bootedRef = useRef(false);
  if (!loading) bootedRef.current = true;

  if (loading && !bootedRef.current) {
    return <BootScreen />;
  }

  if (!user) {
    return <LoginScreen config={config} />;
  }

  return <>{children}</>;
}
